import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { ArrowLeft, Save, Loader2, Lightbulb } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { createTipActionAtom } from '../stores/tipsStore';
import type { TipSource } from '../types/api';

interface NewTipFormData {
  title: string;
  description: string;
  source: TipSource;
  source_url: string;
  source_contact: string;
  priority: number;
  tags: string;
  editorial_notes: string;
}

export function NewTip() {
  const navigate = useNavigate();
  const createTip = useSetAtom(createTipActionAtom);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    setValue,
    watch,
  } = useForm<NewTipFormData>({
    defaultValues: {
      title: '',
      description: '',
      source: 'manual',
      source_url: '',
      source_contact: '',
      priority: 3,
      tags: '',
      editorial_notes: '',
    },
  });

  const onSubmit = async (data: NewTipFormData) => {
    await createTip({
      title: data.title,
      description: data.description || null,
      source: data.source,
      source_url: data.source_url || null,
      source_contact: data.source_contact || null,
      priority: data.priority,
      tags: data.tags || null,
      editorial_notes: data.editorial_notes || null,
    });

    navigate('/inbox');
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/inbox');
  };

  return (
    <div className='h-full flex flex-col'>
      {/* Header with Breadcrumbs */}
      <div className='p-4 border-b bg-muted/30 text-left'>
        <div className='flex items-center justify-between mb-4'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/inbox'>Tip Inbox</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Tip</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Button
            variant='outline'
            size='sm'
            onClick={handleCancel}
            className='gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back
          </Button>
        </div>

        <div className='flex items-center gap-3'>
          <Lightbulb className='h-8 w-8 text-primary' />
          <h1 className='text-2xl font-bold'>Create New Tip</h1>
        </div>

        <p className='text-sm text-muted-foreground mt-2'>
          Fill out the information below to submit a new journalism tip.
        </p>
      </div>

      {/* Form Content */}
      <div className='flex-1 overflow-auto p-4'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='title'>Title *</Label>
                <Input
                  id='title'
                  {...register('title', { required: 'Title is required' })}
                  className={cn(errors.title && 'border-red-500')}
                  placeholder='Brief description of the tip...'
                />
                {errors.title && (
                  <p className='text-sm text-red-500'>{errors.title.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='description'>Description</Label>
                <Textarea
                  id='description'
                  {...register('description')}
                  rows={4}
                  placeholder='Detailed information about the tip...'
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='source'>Source Type</Label>
                  <Select
                    value={watch('source')}
                    onValueChange={(value: TipSource) =>
                      setValue('source', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='manual'>Manual</SelectItem>
                      <SelectItem value='email'>Email</SelectItem>
                      <SelectItem value='linkedin'>LinkedIn</SelectItem>
                      <SelectItem value='rss'>RSS Feed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='priority'>Priority (1-5)</Label>
                  <Select
                    value={watch('priority')?.toString()}
                    onValueChange={value => setValue('priority', Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='1'>1 - Low</SelectItem>
                      <SelectItem value='2'>2 - Below Normal</SelectItem>
                      <SelectItem value='3'>3 - Normal</SelectItem>
                      <SelectItem value='4'>4 - High</SelectItem>
                      <SelectItem value='5'>5 - Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='source_url'>Source URL</Label>
                <Input
                  id='source_url'
                  type='url'
                  {...register('source_url')}
                  placeholder='https://example.com/article'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='source_contact'>Source Contact</Label>
                <Input
                  id='source_contact'
                  {...register('source_contact')}
                  placeholder='Contact name or email'
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='tags'>Tags</Label>
                <Input
                  id='tags'
                  {...register('tags')}
                  placeholder='politics, economy, technology (comma-separated)'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='editorial_notes'>Editorial Notes</Label>
                <Textarea
                  id='editorial_notes'
                  {...register('editorial_notes')}
                  rows={3}
                  placeholder='Internal editorial notes...'
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className='flex items-center justify-between pt-4 border-t'>
            <Button
              type='button'
              variant='outline'
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting} className='gap-2'>
              {isSubmitting ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Creating...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' />
                  Create Tip
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
